import React from "react";
import { isCzechHoliday } from "../../utils/czechHolidays";


// Tooltip pro půlhodiny (z openTime + index)
const slotLabel = (openTime, index) => {
  if (!openTime) return "";
  const [h, m] = openTime.split(":").map(Number);
  const start = h * 60 + m + index * 30;
  const end = start + 30;

  const fmt = (min) =>
    `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

  return `${fmt(start)}–${fmt(end)}`;
};

export default function WeekPlanner({
  weekDays,
  employees,
  shifts,
  updateShift,
  openingConfigs,
  currentMonth,
  readOnly,

  // ✅ constraints z dovolených/požadavků
  leaveConstraintsByDate = {},
  canOverrideConstraints = false,
  // ✅ pro popisky/kolize létačů
  currentPharmacyCode = null,
}) {
  const magisters = employees.filter((e) => e.role === "magistr");
  const assistants = employees.filter((e) => e.role === "assistant");
  const sanitars = employees.filter((e) => e.role === "sanitar");

  // ✅ coverage řádek (agregace za roli) – v readOnly vykreslíme šedě
  const renderRoleSlotsRow = (filledArray, openTime, isDisabled = false) => {
    if (!filledArray || !filledArray.length) return null;

    return (
      <div className="flex mt-1 mb-3">
        {filledArray.map((filled, i) => {
          const isHourEnd = i % 2 === 1;

          const filledCls = isDisabled
            ? filled
              ? "bg-gray-400"
              : "bg-gray-100"
            : filled
            ? "bg-blue-600"
            : "bg-blue-100";

          const borderCls = isDisabled ? "border-gray-200" : "border-blue-200";
          const hourBorderCls = isDisabled
            ? isHourEnd
              ? "border-r-2 border-r-gray-300"
              : "border-r"
            : isHourEnd
            ? "border-r-2 border-r-blue-300"
            : "border-r";

          return (
            <div
              key={i}
              title={slotLabel(openTime, i)}
              className={`
                w-5 h-5
                ${filledCls}
                border ${borderCls}
                ${hourBorderCls}
              `}
            />
          );
        })}
      </div>
    );
  };

  // ✅ výpočet coverage (agregace)
  const computeRoleSlots = (date, roleEmployees) => {
    const cfg = openingConfigs?.[date];
    const slots = cfg?.slots ?? 0;
    if (!slots) return [];

    const filled = Array(slots).fill(false);
    const dayShifts = shifts[date]?.employees || {};

    // ✅ "?" = nejistá směna (typicky asistenti)
    // pravidlo pokrytí čtverečků:
    // - 1× "?" = NEpokryje nic
    // - 2× a více "?" = pokryje celý den (jako C)
    const qCount = (roleEmployees || []).reduce((acc, emp) => {
      const t = dayShifts?.[emp.id]?.type;
      return acc + (t === "?" ? 1 : 0);
    }, 0);

    if (qCount >= 2) return Array(slots).fill(true);

    const getCountForEmp = (emp) => {
      const s = dayShifts[emp.id];
      if (!s) return 0;

      // C = pokrývá celý den (proto je coverage plná)
      if (s.type === "C") return slots;

      // R/O: preferuj uložené hodiny
      if ((s.type === "R" || s.type === "O") && typeof s.hours === "number") {
        return Math.min(slots, Math.round(s.hours * 2));
      }

      // fallback: kmenový fond
      return Math.min(slots, (emp.fullFond || 0) * 2);
    };

    // R a C → zleva
    roleEmployees.forEach((emp) => {
      const type = dayShifts[emp.id]?.type;
      if (type === "R" || type === "C") {
        const count = getCountForEmp(emp);
        const max = Math.min(count, slots);
        for (let i = 0; i < max; i++) filled[i] = true;
      }
      if (type === "C") {
        for (let i = 0; i < slots; i++) filled[i] = true;
      }
    });

    // O a C → zprava
    roleEmployees.forEach((emp) => {
      const type = dayShifts[emp.id]?.type;
      if (type === "O" || type === "C") {
        const count = getCountForEmp(emp);
        const max = Math.min(count, slots);
        for (let i = 0; i < max; i++) filled[slots - 1 - i] = true;
      }
      if (type === "C") {
        for (let i = 0; i < slots; i++) filled[i] = true;
      }
    });

    return filled;
  };

  const getConstraint = (dateIso, empId) => leaveConstraintsByDate?.[dateIso]?.[empId] || null;

  const renderEmployeeRow = (date, emp, disabled) => {
    const selected = shifts[date]?.employees?.[emp.id]?.type || null;

    const c = getConstraint(date, emp.id);
    const hasBlock = !!c?.blocked;
    const requested = c?.request || null;

    const isFlying = !!emp?.isFlying;

    // ✅ kolize pro létače: backend posílá pro daný den, co je ještě možné vybrat
    const freeOptions = isFlying ? emp?.freeOptionsByDate?.[date] : null; // např. ["R","O"] nebo []
    const freeSet = Array.isArray(freeOptions) ? new Set(freeOptions) : null;

    // info o obsazenosti jinde
    const elsewhere = isFlying ? emp?.scheduleByDate?.[date] : null;

    const formatElsewhere = () => {
      if (!elsewhere) return "";
      const curPc = currentPharmacyCode !== null ? String(currentPharmacyCode) : null;

      const toPart = (pc, t) => {
        // pokud je to stejná pobočka jako právě plánujeme, neukazuj "jinde"
        if (curPc && pc && String(pc) === curPc) return null;
        if (pc && t) return `${t} (pob. ${pc})`;
        if (pc) return `pob. ${pc}`;
        if (t) return `${t}`;
        return null;
      };

      if (Array.isArray(elsewhere)) {
        const parts = elsewhere
          .map((x) => {
            if (!x) return null;
            if (typeof x === "string") return x;
            const pc = x.pharmacyCode ?? x.pharmacy ?? x.code ?? "";
            const t = x.type ?? x.shift ?? "";
            return toPart(pc, t);
          })
          .filter(Boolean);
        return parts.join(", ");
      }

      if (typeof elsewhere === "object") {
        const pc = elsewhere.pharmacyCode ?? "";
        const t = elsewhere.type ?? "";
        return toPart(pc, t) || "";
      }

      return String(elsewhere);
    };

    const elsewhereText = formatElsewhere();

    const rowTitleParts = [];
    if (hasBlock && c?.blockTitle) rowTitleParts.push(`NELZE: ${c.blockTitle}`);
    if (requested && c?.requestTitle) rowTitleParts.push(`POŽADAVEK: ${c.requestTitle}`);
    if (isFlying && freeSet && freeSet.size === 0) rowTitleParts.push("KOLIZE: už má směnu jinde");
    if (isFlying && elsewhereText) rowTitleParts.push(`JINDE: ${elsewhereText}`);
    const rowTitle = rowTitleParts.join(" • ");

    if (disabled) {
      return (
        <div key={emp.id} className="flex items-center gap-4 mb-1.5 opacity-40" title={rowTitle}>
          <div className="w-40">
            <div className="text-[15px] truncate">{emp.name}</div>
            {isFlying && elsewhereText ? (
              <div className="text-[11px] text-gray-500 truncate">jinde: {elsewhereText}</div>
            ) : null}
          </div>

          {selected ? (
            <span className="px-2 py-[2px] text-xs rounded-md border bg-gray-100 text-gray-700 border-gray-200">
              {selected}
            </span>
          ) : null}
        </div>
      );
    }

    return (
      <div key={emp.id} className="flex items-center gap-4 mb-1.5" title={rowTitle}>
        <div className="w-40">
          <div className="text-[15px] truncate">{emp.name}</div>

          {isFlying && elsewhereText ? (
            <div className="text-[11px] text-gray-500 truncate">jinde: {elsewhereText}</div>
          ) : null}
        </div>

        <div className="flex gap-1">
          {["R", "O", "C", "?"].map((type) => {
            const isSelected = selected === type;

            let isDisabled = false;
            let btnTitle = "";

            if (hasBlock) {
              btnTitle = c?.blockTitle ? `Nelze plánovat: ${c.blockTitle}` : "Nelze plánovat";
              isDisabled = !canOverrideConstraints;
            } else if (requested) {
              btnTitle = c?.requestTitle ? `Požadavek: ${c.requestTitle}` : "Požadavek zaměstnance";
              if (!canOverrideConstraints && type !== requested) isDisabled = true;
            }

            if (!isDisabled && isFlying) {
              if (freeSet && !freeSet.has(type) && !isSelected) {
                isDisabled = true;
                btnTitle = btnTitle || (elsewhereText ? `Kolize – jinde: ${elsewhereText}` : "Kolize – už má směnu jinde");
              }

              if (!isDisabled && elsewhereText && !isSelected) {
                isDisabled = true;
                btnTitle = btnTitle || `Kolize – jinde: ${elsewhereText}`;
              }
            }

            return (
              <button
                key={type}
                onClick={() => {
                  if (isDisabled) return;
                  updateShift(date, emp.id, type);
                }}
                disabled={isDisabled}
                title={btnTitle}
                className={`
                  px-1.5 py-[2px] border rounded-md text-xs transition
                  ${
                    isSelected
                      ? "bg-blue-600 text-white border-blue-700 shadow-sm"
                      : isDisabled
                      ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                      : "bg-gray-100 text-gray-700 hover:bg-blue-100 border-gray-300"
                  }
                  ${requested === type && !hasBlock ? "ring-1 ring-indigo-300" : ""}
                `}
              >
                {type}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (!weekDays || weekDays.length === 0) {
    return <div className="text-gray-500 mt-6">Vyber týden v kalendáři vpravo.</div>;
  }

  return (
    <div className="space-y-6">
      {weekDays.map((day) => {
        const cfg = openingConfigs?.[day.date];
        const isOutOfMonth = day.jsDate.getMonth() + 1 !== currentMonth;

        const isHoliday = isCzechHoliday(day.date);
        const isClosedByOpeningHours = !cfg || !cfg.open || !cfg.close;

        // když je svátek a openingConfig je zavřený, bereme to jako zavřený svátek
        const isHolidayClosed = isHoliday && isClosedByOpeningHours;

        const isClosed = isClosedByOpeningHours;

        const timeLabel = isHolidayClosed
          ? "Svátek – zavřeno"
          : !isClosedByOpeningHours
          ? `${cfg.open} – ${cfg.close}`
          : "Zavřeno";

        const disabled = readOnly || isOutOfMonth || isClosed;

        const hasSlots = !!(cfg && cfg.open && cfg.close && (cfg.slots ?? 0) > 0);

        const magSlots = hasSlots ? computeRoleSlots(day.date, magisters) : [];
        const asSlots = hasSlots ? computeRoleSlots(day.date, assistants) : [];
        const sanSlots = hasSlots ? computeRoleSlots(day.date, sanitars) : [];

        return (
          <div
            key={day.date}
            className={`border rounded-xl shadow-sm p-5 bg-white ${isOutOfMonth ? "opacity-60" : ""}`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="text-lg font-semibold text-gray-800">{day.label}</div>

                {isHoliday && (
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
                      isHolidayClosed
                        ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}
                  >
                    Svátek
                  </span>
                )}
              </div>

              <div className="text-gray-500 text-sm">
                {isHolidayClosed ? "Svátek – zavřeno" : timeLabel}
              </div>
            </div>

            {magisters.length > 0 && (
              <>
                <div className="font-semibold text-blue-700 text-sm mb-1">Magistři</div>
                {magisters.map((emp) => renderEmployeeRow(day.date, emp, disabled))}
                {hasSlots && renderRoleSlotsRow(magSlots, cfg?.open, disabled)}
                <hr className="my-3" />
              </>
            )}

            {assistants.length > 0 && (
              <>
                <div className="font-semibold text-blue-700 text-sm mb-1">Asistenti</div>
                {assistants.map((emp) => renderEmployeeRow(day.date, emp, disabled))}
                {hasSlots && renderRoleSlotsRow(asSlots, cfg?.open, disabled)}
                <hr className="my-3" />
              </>
            )}

            {sanitars.length > 0 && (
              <>
                <div className="font-semibold text-blue-700 text-sm mb-1">Sanitáři</div>
                {sanitars.map((emp) => renderEmployeeRow(day.date, emp, disabled))}
                {hasSlots && renderRoleSlotsRow(sanSlots, cfg?.open, disabled)}
              </>
            )}

            {disabled && (
              <div className="text-xs text-gray-400 mt-2">
                {isOutOfMonth
                  ? "Tento den je mimo aktuální měsíc."
                  : isHolidayClosed
                  ? "Státní svátek – provoz je zavřený, směny se neplánují."
                  : "Zavřeno nebo není nastavena otevírací doba."}
              </div>
            )}
          </div>
        );
      })} 
    </div>
  );
}